import { FileText } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface EditorHostProps {
  /**
   * The real document editor to mount (docx-editor for Word, Univer for Excel).
   * Supplied in a later task (M3); when absent, a placeholder is rendered. The
   * mounted editor is expected to bind to the shared selection in
   * `useWorkspaceStore` (read `activeQuestionId`/`activeAnchor` to scroll &
   * highlight; call `setActiveQuestion` when the cursor enters an answer).
   */
  editorSlot?: ReactNode;
  className?: string;
}

/**
 * The left pane: a typed slot where a document editor mounts. This keeps the
 * workspace engine-agnostic — the panel and the editor coordinate purely
 * through the shared selection store, so swapping Word for Excel changes only
 * what is passed as `editorSlot`.
 */
export function EditorHost({ editorSlot, className }: EditorHostProps) {
  return (
    <div
      className={cn(
        'flex h-full min-h-[300px] flex-col overflow-hidden rounded-lg border border-border bg-background',
        className,
      )}
    >
      {editorSlot ?? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/60" aria-hidden="true" />
          <p className="text-sm font-medium text-muted-foreground">Document editor mounts here</p>
          <p className="max-w-xs text-xs text-muted-foreground/70">
            The Word or Excel editor is bound to the Q&amp;A index through shared selection state.
          </p>
        </div>
      )}
    </div>
  );
}
